import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Play } from "lucide-react";
import { MaskLines, Overline } from "./motion";
import { useTranslate } from "../context/TranslateContext";
import { STATS } from "../mock/data";

const HERO_IMG =
  "https://images.unsplash.com/photo-1770982726697-309881d78cc1?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const HERO_LISTEN =
  "Welcome to IndiasCinema — a global portal for Indian cinema and music. Discover films, songs, artists and the vast history behind them, across every Indian language.";

export const Hero = ({ onAsk }) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [start, setStart] = useState(false);
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 800], [0, 160]);
  const scaleImg = useTransform(scrollY, [0, 800], [1, 1.15]);
  const yText = useTransform(scrollY, [0, 600], [0, -60]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  useEffect(() => { const timr = setTimeout(() => setStart(true), 250); return () => clearTimeout(timr); }, []);

  return (
    <section id="top" className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Vintage cinema projector and velvet curtain" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/75 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/20 to-transparent" />
      </motion.div>

      <motion.div style={{ y: yText, opacity }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-6 pb-16 pt-32 md:px-12 md:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: start ? 1 : 0 }} transition={{ delay: 0.1 }}>
          <Overline className="!text-[#E6B84D]">Indian Cinema · Television · OTT · Music</Overline>
        </motion.div>

        <h1 className="mt-6 font-display text-6xl font-semibold leading-[0.92] tracking-tight text-paper sm:text-7xl md:text-8xl lg:text-[8.5rem]">
          <MaskLines start={start} lines={["Discover the", ""]} />
          <MaskLines start={start} lines={["soul of "]} lineClassName="inline" />
          <span className="line-mask -mt-2 md:-mt-4">
            <motion.span className="block italic text-gradient-gold"
              initial={{ y: "110%" }} animate={start ? { y: "0%" } : {}}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}>
              Indian Cinema
            </motion.span>
          </span>
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 flex max-w-2xl flex-col gap-6">
          <p className="font-body text-lg text-paper/85 md:text-xl">
            {t("One home for every film, every song, and every artist — across Hindi, Malayalam, Tamil, Telugu and beyond. Built for a billion stories.")}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={() => navigate("/watch")} data-testid="hero-cta-ai"
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-6 py-3 font-body font-medium text-ink transition-transform duration-300 hover:scale-105">
              <Play className="h-4 w-4 fill-ink" /> {t("What should I watch?")}
            </button>
            <a href="#movies" data-testid="hero-cta-explore"
              className="inline-flex items-center gap-2 rounded-full border border-paper/50 px-6 py-3 font-body text-paper transition-colors duration-300 hover:bg-paper/10">
              {t("Explore the universe")}
            </a>
            
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={start ? { opacity: 1 } : {}} transition={{ delay: 1.3, duration: 1 }}
          className="mt-16 grid grid-cols-2 gap-8 border-t border-white/25 pt-8 md:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-display text-4xl font-semibold text-[#E6B84D] md:text-5xl">{s.value}</div>
              <div className="mt-1 font-body text-xs uppercase tracking-widest text-paper/70">{t(s.label)}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity }} className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
        <ArrowDown className="h-6 w-6 animate-bounce text-gold/70" />
      </motion.div>
    </section>
  );
};
